import { formatISO, format } from "date-fns";
import intlFormat from "date-fns/intlFormat";
import { CustomDate } from "../models/custom-date/custom-date";

// MUI TextField requires a specific format
export const formatDateToMUIFormat = (date: Date | null = new Date()): string => {
  const d = new Date(date ?? new Date());

  const isUTC = date instanceof Date && date?.toDateString().endsWith("Z");

  const dd = isUTC ? d.getUTCDate() : d.getDate();
  const mm = isUTC ? d.getUTCMonth() + 1 : d.getMonth() + 1;
  const yyyy = isUTC ? d.getUTCFullYear() : d.getFullYear();

  return `${dd < 10 ? `0${dd}` : dd}.${mm < 10 ? `0${mm}` : mm}.${yyyy}`;
};

export const formatDateToMUIFormatMonth = (date: Date = new Date()): string => {
  const d = new Date(date);
  const isUTC = date instanceof Date && date.toDateString().endsWith("Z");

  const mm = isUTC ? d.getUTCMonth() + 1 : d.getMonth() + 1;
  const yyyy = isUTC ? d.getUTCFullYear() : d.getFullYear();

  return `${mm < 10 ? `0${mm}` : mm}.${yyyy}`;
};

export const formatDateToMUIFormatShort = (date: Date = new Date()): string => {
  const d = new Date(date);

  const isUTC = date instanceof Date && date.toDateString().endsWith("Z"); //We check if date is instance of date because the server might return a date without timezone and thus Js does not considered as date

  const mm = isUTC ? d.getUTCMonth() + 1 : d.getMonth() + 1;
  const yyyy = isUTC ? d.getUTCFullYear() : d.getFullYear();

  return `${yyyy} ${mm < 10 ? `0${mm}` : mm}`;
};

export const intlFormatDate = (date: string | Date): string =>
  date
    ? intlFormat(
        new Date(date),
        { day: "2-digit", month: "2-digit", year: "2-digit" },
        { locale: localStorage.getItem("i18nextLng") ?? "en" }
      )
    : "";

export const intlFormatDateMonth = (date: string | Date): string =>
  date
    ? intlFormat(
        new Date(date),
        { day: "2-digit", month: "short" },
        { locale: localStorage.getItem("i18nextLng") ?? "en" }
      )
    : "";

export const intlFormatDateISO = (date: string | Date): string =>
  date ? formatISO(new Date(date), { format: "extended", representation: "date" }) : "";

export const intlFormatDateShort = (date: Date): string =>
  date
    ? intlFormat(
        new Date(date),
        { day: "2-digit", month: "short", year: "numeric" },
        { locale: localStorage.getItem("i18nextLng") ?? "en" }
      )
    : "";

export const intlFormatDateWithHour = (date: Date): string =>
  date
    ? intlFormat(
        new Date(date),
        {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        },
        {
          locale: localStorage.getItem("i18nextLng") ?? "en",
        }
      )
    : "";

export const intlFormatDateToHour = (date: Date): string =>
  date
    ? intlFormat(
        new Date(date),
        {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        },
        { locale: localStorage.getItem("i18nextLng") ?? "en" }
      )
    : "";

export const getWeekdayAndDate = (date: Date) => {
  if (!date) return "";

  const weekday = intlFormat(date, { weekday: "short" }, { locale: localStorage.getItem("i18nextLng") ?? "en" });

  const day = intlFormat(date, { day: "2-digit" }, { locale: localStorage.getItem("i18nextLng") ?? "en" });

  return `${weekday} ${day}`;
};

export const computeMonthName = (monthNumber: number) => {
  const year = new Date().getFullYear();

  return new Intl.DateTimeFormat(localStorage.getItem("i18nextLng") ?? "en", {
    month: "long",
  }).format(new Date(year, monthNumber));
};

export const shortMonthName = (monthNumber: number) => {
  const year = new Date().getFullYear();

  return new Intl.DateTimeFormat(localStorage.getItem("i18nextLng") ?? "en", {
    month: "short",
  }).format(new Date(year, monthNumber));
};

export const getDayAndWeekday = (date: Date) =>
  date
    ? intlFormat(date, { day: "numeric", weekday: "short" }, { locale: localStorage.getItem("i18nextLng") ?? "en" })
    : "";

export const getDayAndMonth = (date: Date) =>
  date
    ? intlFormat(date, { day: "numeric", month: "short" }, { locale: localStorage.getItem("i18nextLng") ?? "en" })
    : "";

export const getOnlyTime = (date: Date) => {
  const newTime = new Date(date);
  const hour = newTime.getHours();
  const resultHour = hour < 10 ? `0${hour}` : hour;
  const minutes = newTime.getMinutes();
  const resultMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${resultHour}:${resultMinutes}`;
};

export const getNormaleTime = (hour: number, minute: number) => {
  const resultHour = hour < 10 ? `0${hour}` : hour;
  const resultMinutes = minute < 10 ? `0${minute}` : minute;
  return `${resultHour}:${resultMinutes}`;
};

export const getNormaleDateOrMonth = (dateMonth: number) => {
  return `${dateMonth < 10 ? `0${dateMonth}` : dateMonth}`;
};

export const numberWithApostrophe = (num: number) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

export const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

export const fillArrayWithDates = (from: CustomDate, to: CustomDate) => {
  const yearDiff = to.year - from.year;

  if (yearDiff < 0) return [];

  if (yearDiff === 0) {
    return Array.from(
      {
        length: to.month - from.month + 1,
      },
      (item, index) => {
        return {
          year: from.year,
          month: index + from.month,
          // days: getDaysInMonth(from.year, from.month - 1),
          days: Array.from({ length: getDaysInMonth(from.year, from.month - 1) }, (_, i) => i + 1),
        };
      }
    );
  }

  if (yearDiff === 1) {
    const arrayFrom = Array.from(
      {
        length: 12 - from.month + 1,
      },
      (item, index) => {
        return {
          year: from.year,
          month: index + from.month,
          // days: getDaysInMonth(from.year, index + from.month - 1),
          days: Array.from({ length: getDaysInMonth(from.year, index + from.month - 1) }, (_, i) => i + 1),
        };
      }
    );

    const arrayTo = Array.from(
      {
        length: to.month,
      },
      (item, index) => {
        return {
          year: to.year,
          month: index + 1,
          // days: getDaysInMonth(to.year, index),
          days: Array.from({ length: getDaysInMonth(to.year, index) }, (_, i) => i + 1),
        };
      }
    );

    return arrayFrom.concat(arrayTo);
  }

  if (yearDiff > 1) {
    const arrayFrom = Array.from(
      {
        length: 12 - from.month + 1,
      },
      (item, index) => {
        return {
          year: from.year,
          month: index + from.month,
          // days: getDaysInMonth(from.year, index + from.month - 1),
          days: Array.from({ length: getDaysInMonth(from.year, index + from.month - 1) }, (_, i) => i + 1),
        };
      }
    );

    const arrayTo = Array.from(
      {
        length: to.month,
      },
      (item, index) => {
        return {
          year: to.year,
          month: index + 1,
          // days: getDaysInMonth(to.year, index),
          days: Array.from({ length: getDaysInMonth(to.year, index) }, (_, i) => i + 1),
        };
      }
    );

    const arrayWithOtherDates = Array.from(
      {
        length: yearDiff - 1,
      },
      (item, index) => from.year + index + 1
    ).map((year) => {
      return Array.from(
        {
          length: 12,
        },
        (item, index) => index + 1
      ).map((month) => {
        return {
          year: year,
          month: month,
          // days: getDaysInMonth(year, month - 1),
          days: Array.from({ length: getDaysInMonth(year, month - 1) }, (_, i) => i + 1),
        };
      });
    });

    return arrayFrom.concat(...arrayWithOtherDates).concat(arrayTo);
  }
};

export const isValidDate = (date: any) => {
  const isDate =
    date &&
    (Object.prototype.toString.call(date) === "[object Date]" ||
      Object.prototype.toString.call(date) === "[object String]");

  let result = false;

  if (isDate) {
    result = new Date(date).getFullYear().toString().length > 3;
  }

  return result;
};

export const convertDateToUTC = (date: Date) => {
  const d = new Date(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()));
};

export const areDatesTheSame = (firstDate: Date, secondDate: Date) => {
  return new Date(firstDate).getTime() === new Date(secondDate).getTime();
};

export const areDatesWithoutHoursTheSame = (firstDate: Date, secondDate: Date) => {
  const dateOne = new Date(firstDate);
  const dateTwo = new Date(secondDate);
  return (
    dateOne.getFullYear() === dateTwo.getFullYear() &&
    dateOne.getMonth() === dateTwo.getMonth() &&
    dateOne.getDate() === dateTwo.getDate()
  );
};

export const listOfMonths: { [key: number]: string } = {
  0: "january",
  1: "february",
  2: "march",
  3: "april",
  4: "may",
  5: "june",
  6: "july",
  7: "august",
  8: "september",
  9: "october",
  10: "november",
  11: "december",
};
